import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import {
  Instagram as InstagramIcon,
  PersonOutline as PersonIcon,
  EmailOutlined as EmailIcon,
  CheckCircleOutline as CheckCircleIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { useInView } from 'react-intersection-observer';
import { joinCommunityAsync } from '../../redux/communitySlice';

const CommunityJoin = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.community);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    instagram: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.email.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide both your name and email.',
        severity: 'warning'
      });
      return;
    }

    const resultAction = await dispatch(
      joinCommunityAsync({
        name: formData.fullName.trim(),
        email: formData.email.trim(),
        instagram: formData.instagram.trim()
      })
    );

    if (joinCommunityAsync.fulfilled.match(resultAction)) {
      setSnackbar({
        open: true,
        message: resultAction.payload?.message || 'Welcome to the AAGC Community! We will be in touch soon.',
        severity: 'success'
      });
      setFormData({ fullName: '', email: '', instagram: '' });
    } else {
      setSnackbar({
        open: true,
        message: resultAction.payload || 'Failed to join the community.',
        severity: 'error'
      });
    }
  };

  const inputStyles = {
    '& .MuiFilledInput-root': {
      bgcolor: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '12px',
      color: '#ffffff',
      transition: 'all 0.3s ease',
      '&:hover': {
        bgcolor: 'rgba(255, 255, 255, 0.06)',
        borderColor: 'rgba(255, 255, 255, 0.2)',
      },
      '&.Mui-focused': {
        bgcolor: 'rgba(255, 255, 255, 0.08)',
        borderColor: '#00f2ff',
        boxShadow: '0 0 20px rgba(0, 242, 255, 0.1)',
      },
      '&:before, &:after': {
        display: 'none',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#94a3b8',
      '&.Mui-focused': {
        color: '#00f2ff',
      },
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: '#64748b',
      transition: 'color 0.3s ease',
    },
    '& .Mui-focused .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: '#00f2ff',
    },
  };

  return (
    <Box
      ref={ref}
      sx={{
        bgcolor: '#0a0a0a',
        py: { xs: 4, md: 5 },
        position: 'relative',
        overflow: 'hidden',
        mb: 2,
        background: 'radial-gradient(circle at 50% 50%, #083344 0%, #0a0a0a 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <Paper
            elevation={0}
            sx={{
              py: { xs: 4, md: 3 },
              px: { xs: 3, md: 3 },
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.01) 100%)',
              backdropFilter: 'blur(40px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: { xs: '24px' },
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: 'Bebas Neue',
                      color: '#00f2ff',
                      fontSize: { xs: '2rem', md: '2.1rem' },
                      lineHeight: 1,
                      mb: 0.5,
                      letterSpacing: '1px'
                    }}
                  >
                    JOIN THE <Box component="span" sx={{ color: '#ffffff' }}>COMMUNITY</Box>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                    }}
                  >
                    Get updates, early access, and behind-the-scenes action.
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid size={{ xs: 12, sm: 4, md: 3.5 }}>
                      <TextField
                        fullWidth
                        label="Name"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        variant="filled"
                        required
                        sx={inputStyles}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon sx={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.4)' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4, md: 3.5 }}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        variant="filled"
                        required
                        sx={inputStyles}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon sx={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.4)' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4, md: 3.5 }}>
                      <TextField
                        fullWidth
                        label="Instagram"
                        name="instagram"
                        value={formData.instagram}
                        onChange={handleChange}
                        variant="filled"
                        placeholder="@username"
                        sx={inputStyles}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <InstagramIcon sx={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.4)' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 1.5 }}>
                      <Button
                        fullWidth
                        type="submit"
                        disabled={loading}
                        variant="contained"
                        sx={{
                          height: '56px',
                          bgcolor: '#00f2ff',
                          color: '#000000',
                          fontSize: '1.1rem',
                          fontFamily: 'Bebas Neue',
                          letterSpacing: '1px',
                          borderRadius: '12px',
                          boxShadow: '0 4px 15px rgba(0, 242, 255, 0.2)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: '#00ddec',
                            transform: 'translateY(-2px)',
                          },
                          '&.Mui-disabled': {
                            bgcolor: 'rgba(0, 242, 255, 0.3)',
                            color: 'rgba(0,0,0,0.5)'
                          }
                        }}
                      >
                        {loading ? <CircularProgress size={20} color="inherit" /> : 'JOIN'}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            bgcolor: '#00f2ff',
            color: '#000000',
            fontWeight: 600,
            '& .MuiAlert-icon': { color: '#000000' }
          }}
          icon={<CheckCircleIcon />}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommunityJoin;
